import api from "./axios";
import type { CabinGroup, Division } from "../types";

export const cabinGroupsApi = {
  list: async (): Promise<CabinGroup[]> => {
    const res = await api.get<CabinGroup[]>("/cabin-groups");
    return res.data;
  },

  get: async (id: number, year?: number): Promise<CabinGroup> => {
    const res = await api.get<CabinGroup>(`/cabin-groups/${id}`, {
      params: year ? { year } : undefined,
    });
    return res.data;
  },

  create: async (data: Omit<CabinGroup, "id" | "division">): Promise<CabinGroup> => {
    const res = await api.post<CabinGroup>("/cabin-groups", data);
    return res.data;
  },

  update: async (id: number, data: Partial<Omit<CabinGroup, "id" | "division">>): Promise<CabinGroup> => {
    const res = await api.put<CabinGroup>(`/cabin-groups/${id}`, data);
    return res.data;
  },

  archive: async (id: number): Promise<CabinGroup> => {
    const res = await api.patch<CabinGroup>(`/cabin-groups/${id}/archive`);
    return res.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/cabin-groups/${id}`);
  },

  reorderCabinGroups: async (orderedIds: number[]): Promise<void> => {
    await api.put("/cabin-groups/reorder", { orderedIds });
  },

  assignCounselor: async (id: number, userId: number): Promise<void> => {
    await api.patch(`/cabin-groups/${id}/assign-counselor`, { userId });
  },

  removeCounselor: async (id: number, userId: number): Promise<void> => {
    await api.patch(`/cabin-groups/${id}/remove-counselor`, { userId });
  },

  assignCamper: async (id: number, camperId: number, year: number): Promise<void> => {
    await api.patch(`/cabin-groups/${id}/assign-camper`, { camperId, year });
  },

  removeCamper: async (id: number, camperId: number, year: number): Promise<void> => {
    await api.patch(`/cabin-groups/${id}/remove-camper`, { camperId, year });
  },

  // ─── Divisions ──────────────────────────────────────────────────────────────

  listDivisions: async (includeArchived = false): Promise<Division[]> => {
    const res = await api.get<Division[]>("/divisions", {
      params: includeArchived ? { includeArchived: "true" } : undefined,
    });
    return res.data;
  },

  createDivision: async (data: Omit<Division, "id" | "cabinGroups" | "sortOrder">): Promise<Division> => {
    const res = await api.post<Division>("/divisions", data);
    return res.data;
  },

  updateDivision: async (id: number, data: Partial<Omit<Division, "id" | "cabinGroups">>): Promise<Division> => {
    const res = await api.put<Division>(`/divisions/${id}`, data);
    return res.data;
  },

  archiveDivision: async (id: number): Promise<Division> => {
    const res = await api.patch<Division>(`/divisions/${id}/archive`);
    return res.data;
  },

  deleteDivision: async (id: number): Promise<void> => {
    await api.delete(`/divisions/${id}`);
  },

  reorderDivisions: async (orderedIds: number[]): Promise<void> => {
    await api.put("/divisions/reorder", { orderedIds });
  },

  mapBunkArea: async (divisionId: number, campMinderBunkAreaId: number | null): Promise<void> => {
    await api.patch(`/divisions/${divisionId}/map-bunk-area`, { campMinderBunkAreaId });
  },
};
